/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import Close from '@mui/icons-material/Close';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import PushPin from '@mui/icons-material/PushPin';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
import SortByAlpha from '@mui/icons-material/SortByAlpha';

import { MaterialIcon } from '../../../components/icons';
import { Translate } from '../../../components/localization';
import SearchFilter, { getFilteredDataByProperties } from '../../../components/form/search-filter';
import { sortAndShiftHomePage } from './util';
import { useHotkey } from '../../../HotKeyContext';
import { useUserContext } from '../../../components/user';
import { useEntityContext } from '../../../util/entity-provider/context';
import {
  CDS_MENU_FAVORITES_UPDATED,
  getMenuFavoritesKey,
  orderItemsByFavorites,
  readMenuFavorites,
} from '../../../util/menu-favorites';
import { MobileBackOption } from './styles';

const SORT_VALUES = [
  { icon: SortByAlpha, sortKey: 'alpha' },
  { icon: FormatListNumbered, sortKey: 'default' },
];

const SubMenuContent = ({
  activatedSubMenu,
  isMobile,
  clearActivatedSubMenu,
  enableSearch,
  amIActive,
  handleMenuItemClick,
  toggleSidebar,
  isSubMenuOpen,
  panelVariant = 'mobile',
  isPanelPinned = false,
  onPinToggle,
  onRequestClosePanel,
  menuTitleDomId,
}) => {
  const intl = useIntl();
  const isPushDesktop = panelVariant === 'push-desktop';
  const [querySearch, setQuerySearch] = useState({});
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);
  const [autoFocusEnabled, setAutoFocusEnabled] = useState(false);
  const [sortMode, setSortMode] = useState(activatedSubMenu?.sortMode);
  const [favoritesRevision, setFavoritesRevision] = useState(0);

  const favoritesEnabled = Boolean(activatedSubMenu?.enableFavorites);
  const { user } = useUserContext();
  const { entityRef } = useEntityContext();
  const userIdentityKey = user?.email || user?.id || user?.username || 'anon';

  const listRef = useRef(null);
  const panelRef = useRef(null);

  const hideSortableSubMenu = activatedSubMenu?.hideSortControls;
  const finalQuerySearch = querySearch[activatedSubMenu?.id] || '';
  const isSearching = Boolean(finalQuerySearch);

  const favoritesKey = useMemo(
    () =>
      favoritesEnabled ? getMenuFavoritesKey({ entityRef, userIdentityKey, groupId: activatedSubMenu?.id }) : null,
    [favoritesEnabled, entityRef, userIdentityKey, activatedSubMenu?.id]
  );

  const favoriteIds = useMemo(
    () => (favoritesKey ? readMenuFavorites(favoritesKey) : []),
    [favoritesKey, favoritesRevision] // eslint-disable-line react-hooks/exhaustive-deps -- revision busts cache on writes
  );

  useEffect(() => {
    if (!favoritesKey) return;

    const reload = () => setFavoritesRevision(revision => revision + 1);

    window.addEventListener(CDS_MENU_FAVORITES_UPDATED, reload);
    window.addEventListener('storage', reload);

    return () => {
      window.removeEventListener(CDS_MENU_FAVORITES_UPDATED, reload);
      window.removeEventListener('storage', reload);
    };
  }, [favoritesKey]);

  const sortedChildren = useMemo(
    () => sortAndShiftHomePage(activatedSubMenu?.children || [], sortMode),
    [activatedSubMenu?.children, sortMode]
  );

  const searchFilteredOptions = useMemo(
    () => getFilteredDataByProperties({ data: sortedChildren, query: finalQuerySearch, properties: ['label'] }),
    [sortedChildren, finalQuerySearch]
  );

  const { homeItems, favoriteItems, otherItems } = useMemo(() => {
    const home = [];
    const rest = [];

    searchFilteredOptions.forEach(item => (item?.isHomePage ? home.push(item) : rest.push(item)));

    if (!favoritesEnabled) {
      return { homeItems: home, favoriteItems: [], otherItems: rest };
    }

    const { favorites, others } = orderItemsByFavorites(rest, favoriteIds);

    return { homeItems: home, favoriteItems: favorites, otherItems: others };
  }, [searchFilteredOptions, favoritesEnabled, favoriteIds]);

  const filteredMenuOptions = useMemo(
    () => [...homeItems, ...favoriteItems, ...otherItems],
    [homeItems, favoriteItems, otherItems]
  );

  const showFavoritesLayout = favoritesEnabled && !isSearching && favoriteItems.length > 0;

  const favoriteIdsSignature = favoritesEnabled ? favoriteIds.join('\0') : '';

  useEffect(() => {
    setSortMode(activatedSubMenu?.sortMode);
    setFocusedRowIndex(0);
    setAutoFocusEnabled(isPushDesktop && Boolean(activatedSubMenu?.id) && !enableSearch);
  }, [activatedSubMenu?.id, activatedSubMenu?.sortMode, isPushDesktop, enableSearch]);

  useEffect(() => {
    if (!favoritesEnabled) return;

    setFocusedRowIndex(0);
  }, [favoritesEnabled, favoriteIdsSignature]);

  useEffect(() => {
    setFocusedRowIndex(prev => {
      if (!filteredMenuOptions.length) return 0;
      if (prev > filteredMenuOptions.length - 1) return filteredMenuOptions.length - 1;

      return prev;
    });
  }, [filteredMenuOptions.length]);

  useEffect(() => {
    if (!isPushDesktop || !autoFocusEnabled || !listRef.current) return;

    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const activeTag = active?.tagName;

    if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || active?.isContentEditable) return;

    const id = window.requestAnimationFrame(() => {
      const nodes = listRef.current.querySelectorAll('button.menu-option');
      const el = nodes[focusedRowIndex];

      if (el && typeof el.focus === 'function') el.focus();
    });

    return () => window.cancelAnimationFrame(id);
  }, [autoFocusEnabled, focusedRowIndex, isPushDesktop]);

  const handleOnSearch = useCallback(
    query => {
      setQuerySearch(prev => ({ ...prev, [activatedSubMenu?.id]: query }));
    },
    [activatedSubMenu?.id]
  );

  const onEnterPress = useCallback(() => {
    if (!activatedSubMenu || !isSubMenuOpen) return;
    if (!filteredMenuOptions.length) return;

    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const panelScope = panelRef.current || listRef.current?.parentElement;

    if (!active || !panelScope?.contains(active)) return;

    const focusedButton = active.closest?.('button.menu-option');
    const focusedTag = active.tagName;
    const focusedIsInput = focusedTag === 'INPUT' || focusedTag === 'TEXTAREA' || active.isContentEditable;

    let target;

    if (focusedButton && listRef.current?.contains(focusedButton)) {
      const buttons = Array.from(listRef.current.querySelectorAll('button.menu-option'));
      const idx = buttons.indexOf(focusedButton);

      target = filteredMenuOptions[idx];
    } else if (focusedIsInput) {
      target = filteredMenuOptions[0];
    }

    if (!target) return;

    handleMenuItemClick(target.path);

    if (!isPanelPinned) clearActivatedSubMenu();

    setQuerySearch({});

    if (isMobile) toggleSidebar();
  }, [
    activatedSubMenu,
    clearActivatedSubMenu,
    filteredMenuOptions,
    handleMenuItemClick,
    isMobile,
    isPanelPinned,
    isSubMenuOpen,
    toggleSidebar,
  ]);

  const hotkeys = useMemo(
    () => [{ key: 'Enter', callback: onEnterPress, options: { allowHotkeysInInputs: true } }],
    [onEnterPress]
  );

  useHotkey(hotkeys);

  const handleLeafClick = useCallback(
    path => {
      handleMenuItemClick(path);

      if (!isPanelPinned) clearActivatedSubMenu();

      if (isMobile) toggleSidebar();
    },
    [clearActivatedSubMenu, handleMenuItemClick, isMobile, isPanelPinned, toggleSidebar]
  );

  const focusRow = useCallback(
    nextIndex => {
      const len = filteredMenuOptions.length;

      if (!len) return;

      const clamped = Math.min(Math.max(0, nextIndex), len - 1);

      setAutoFocusEnabled(true);
      setFocusedRowIndex(clamped);
    },
    [filteredMenuOptions.length]
  );

  const handleRowKeyDown = useCallback(
    (e, index) => {
      if (!isPushDesktop) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        focusRow(index + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        focusRow(index - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        focusRow(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        focusRow(filteredMenuOptions.length - 1);
      }
    },
    [filteredMenuOptions.length, focusRow, isPushDesktop]
  );

  const panelNavLabel = useMemo(() => {
    if (!activatedSubMenu?.labelStringId) return undefined;

    return intl.formatMessage({ id: activatedSubMenu.labelStringId });
  }, [activatedSubMenu?.labelStringId, intl]);

  const pushHeader =
    isPushDesktop && activatedSubMenu ? (
      <div className="push-panel-header">
        <div className="push-panel-title" id={menuTitleDomId}>
          {activatedSubMenu.icon || activatedSubMenu.iconName ? (
            <MaterialIcon icon={activatedSubMenu.icon} iconName={activatedSubMenu.iconName} />
          ) : null}
          <Translate stringId={activatedSubMenu?.labelStringId} />
        </div>
        <div className="push-panel-actions">
          <button
            type="button"
            className="push-panel-icon-button"
            data-submenu-pin
            aria-pressed={isPanelPinned}
            onClick={onPinToggle}
          >
            <MaterialIcon icon={isPanelPinned ? PushPin : PushPinOutlined} />
          </button>
          <button type="button" className="push-panel-icon-button" data-submenu-close onClick={onRequestClosePanel}>
            <MaterialIcon icon={Close} />
          </button>
        </div>
      </div>
    ) : null;

  const sortBlock = !hideSortableSubMenu ? (
    <div className="menu-order-container" role="group">
      {SORT_VALUES.map(sortValue => (
        <button
          type="button"
          key={sortValue.sortKey}
          className={cn('menu-order-icon', { 'menu-order-icon__active': sortMode === sortValue.sortKey })}
          onClick={() => setSortMode(sortValue.sortKey)}
          aria-pressed={sortMode === sortValue.sortKey}
        >
          <MaterialIcon icon={sortValue.icon} fontSize="small" />
        </button>
      ))}
    </div>
  ) : null;

  const searchBlock = enableSearch ? (
    <div className="menu-search-container">
      <SearchFilter fullWidth onSearchChange={handleOnSearch} querySearch={finalQuerySearch} isMobile={isMobile} />
    </div>
  ) : null;

  const legacyTitle = !isPushDesktop ? (
    <div className="menu-title-container">
      {activatedSubMenu?.icon || activatedSubMenu?.iconName ? (
        <MaterialIcon icon={activatedSubMenu.icon} iconName={activatedSubMenu.iconName} />
      ) : null}
      <Translate stringId={activatedSubMenu?.labelStringId} />
    </div>
  ) : null;

  const favoritesHeader = (
    <div className="menu-favorites-header" aria-hidden>
      <Translate stringId="masterDataHome_favorites" defaultMessage="Favorites" />
    </div>
  );

  const favoritesSeparator = <div className="menu-favorites-separator" aria-hidden />;

  const renderMobileRow = (subMenuItem, index) => {
    const key = subMenuItem?.labelStringId ? `${subMenuItem.labelStringId}-${index}` : `item-${index}`;

    return (
      <div
        key={key}
        className={cn('menu-option', {
          'menu-option__active': amIActive(subMenuItem),
          'is-mobile': isMobile,
        })}
        onClick={() => handleLeafClick(subMenuItem.path)}
      >
        <Translate stringId={subMenuItem?.labelStringId} />
      </div>
    );
  };

  const renderPushRow = (subMenuItem, index) => {
    const rowTitle = subMenuItem?.labelStringId ? intl.formatMessage({ id: subMenuItem.labelStringId }) : '';
    const key = subMenuItem?.labelStringId ? `${subMenuItem.labelStringId}-${index}` : `item-${index}`;

    return (
      <button
        type="button"
        key={key}
        className={cn('menu-option', { 'menu-option__active': amIActive(subMenuItem) })}
        tabIndex={focusedRowIndex === index ? 0 : -1}
        title={rowTitle || undefined}
        onClick={() => handleLeafClick(subMenuItem.path)}
        onKeyDown={e => handleRowKeyDown(e, index)}
      >
        {subMenuItem.icon || subMenuItem.iconName ? (
          <MaterialIcon icon={subMenuItem.icon} iconName={subMenuItem.iconName} fontSize="small" />
        ) : null}
        <span className="menu-option-label">
          <Translate stringId={subMenuItem?.labelStringId} />
        </span>
        {subMenuItem.badge != null ? <span className="menu-option-badge">{subMenuItem.badge}</span> : null}
      </button>
    );
  };

  const renderOptions = renderRow => {
    if (!showFavoritesLayout) {
      return filteredMenuOptions?.map((item, index) => renderRow(item, index));
    }

    const favoritesStart = homeItems.length;
    const othersStart = favoritesStart + favoriteItems.length;

    return (
      <>
        {homeItems.map((item, index) => renderRow(item, index))}
        {favoritesHeader}
        {favoriteItems.map((item, index) => renderRow(item, favoritesStart + index))}
        {favoritesSeparator}
        {otherItems.map((item, index) => renderRow(item, othersStart + index))}
      </>
    );
  };

  const optionsBlockMobile = (
    <div className="menu-options-container" ref={listRef}>
      {renderOptions(renderMobileRow)}
    </div>
  );

  const optionsBlockPush = (
    <div className="menu-options-container" ref={listRef}>
      {renderOptions(renderPushRow)}
    </div>
  );

  const handleMobileBack = () => {
    clearActivatedSubMenu();
  };

  const mobileBack = isMobile ? (
    <MobileBackOption className={cn('menu-option', { 'is-mobile': isMobile })} onClick={handleMobileBack}>
      <MaterialIcon icon={ArrowBackRounded} />
    </MobileBackOption>
  ) : null;

  if (isPushDesktop) {
    return (
      <nav ref={panelRef} className="push-panel-body" aria-label={panelNavLabel}>
        {pushHeader}
        <div className="push-panel-body-scroll">
          {sortBlock}
          {searchBlock}
          {optionsBlockPush}
        </div>
        <div className="push-panel-footer" aria-hidden />
      </nav>
    );
  }

  return (
    <>
      {mobileBack}
      {legacyTitle}
      {sortBlock}
      {searchBlock}
      {optionsBlockMobile}
    </>
  );
};

export default SubMenuContent;
